import { useLocation } from 'react-router-dom'
import PreservedLink from '@/components/shared/PreservedLink'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

const SITE_URL = 'https://claims1.co.uk'

const DynamicBreadcrumb = () => {
  const location = useLocation()

  // Generate breadcrumb items from pathname
  const pathSegments = location.pathname
    .split('/')
    .filter(segment => segment !== '')

  // Create breadcrumb items with proper labels
  const getBreadcrumbLabel = (segment, index, segments) => {
    // Handle specific route patterns
    if (segments[0] === 'lenders' && segment.startsWith('mis-sold-')) {
      return (
        segment
          .replace('mis-sold-', '')
          .replace('-car-finance', '')
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') + ' Finance'
      )
    }

    if (segments[0] === 'vehicles' && segment.endsWith('-car-finance')) {
      return (
        segment
          .replace('-car-finance', '')
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') + ' Vehicles'
      )
    }

    if (
      segments[0] === 'blogs' &&
      segment ===
        'data-deletion-threatens-1bn-car-finance-compensation-uk-borrowers'
    ) {
      return 'Data Deletion Threatens £1bn Car Finance Compensation'
    }

    // Handle general segments
    switch (segment) {
      case 'about-us':
        return 'About Us'
      case 'faqs':
        return 'FAQs'
      case 'customer-review':
        return 'Customer Reviews'
      case 'lenders':
        return 'Lenders'
      case 'vehicles':
        return 'Vehicles'
      case 'blogs':
        return 'Blogs'
      default:
        return segment
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
    }
  }

  // Build breadcrumb path
  const breadcrumbItems = []
  let currentPath = ''

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`
    const label = getBreadcrumbLabel(segment, index, pathSegments)
    const isLast = index === pathSegments.length - 1

    breadcrumbItems.push({
      label,
      path: currentPath,
      isLast,
    })
  })

  // Generate JSON-LD structured data for search engines
  const generateBreadcrumbJsonLd = () => {
    const baseUrl = SITE_URL
    const breadcrumbList = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
    ]

    breadcrumbItems.forEach((item, index) => {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: index + 2,
        name: item.label,
        item: `${baseUrl}${item.path}`,
      })
    })

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbList,
    }
  }

  return (
    <>
      {/* JSON-LD structured data for search engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateBreadcrumbJsonLd()),
        }}
      />

      {/* Visually hidden breadcrumbs for SEO - screen readers and search engines can access */}
      <div className="sr-only" aria-label="Breadcrumb navigation">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <PreservedLink to="/">Home</PreservedLink>
              </BreadcrumbLink>
            </BreadcrumbItem>

            {breadcrumbItems.map((item, _index) => (
              <div key={item.path} className="flex items-center">
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {item.isLast ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <PreservedLink to={item.path}>{item.label}</PreservedLink>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </div>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </>
  )
}

export default DynamicBreadcrumb
